import { FilterValue } from "../models/Filters/FilterValue";
import { NewAPIResponse } from "../models/NetworkResponse";
import { getAPI } from "./Networking";
import {
  LeadStageAveragesResponse,
  LeadStageStatusSummaryResponse,
  LeadStageSummaryListResponse,
  LeadStageSummaryPaginatedResponse,
} from "../models/Dashboard/LeadAnalytics/LeadAnalytics";
import { LeadAnalyticsFilters } from "../models/Dashboard/LeadAnalytics/LeadAnalyticsFilters";

export default class LeadAnalyticsAPI {
  private buildQueryParams = (
    filters: LeadAnalyticsFilters,
    includeCellFilters = false,
  ): URLSearchParams => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.owner_id.length > 0) {
        queryParams.append("owner_id", filters.owner_id.join(","));
      }
      if (filters.pipeline_id.length > 0) {
        queryParams.append("pipeline_id", filters.pipeline_id.join(","));
      }
      if (filters.source_id.length > 0) {
        queryParams.append("source_id", filters.source_id.join(","));
      }
      if (filters.start_date) {
        queryParams.append("start_date", filters.start_date.format("YYYY-MM-DD"));
      }
      if (filters.end_date) {
        queryParams.append("end_date", filters.end_date.format("YYYY-MM-DD"));
      }

      queryParams.append("section", filters.section);
      queryParams.append("page_no", String(filters.page_no));
      queryParams.append("limit", String(filters.limit));
      queryParams.append("sort_by", filters.sort_by);
      queryParams.append("sort_type", filters.sort_type);

      if (includeCellFilters && filters.final_status) {
        queryParams.append("status", filters.final_status);
        queryParams.append("final_status", filters.final_status);
        if (filters.stage_pipeline_id) {
          queryParams.append("stage_pipeline_id", String(filters.stage_pipeline_id));
        }
        if (filters.conversion_probability != null) {
          queryParams.append("conversion_probability", String(filters.conversion_probability));
        }
      }

      return queryParams;
    } catch (error) {
      throw error;
    }
  };

  getLeadStageSummary = async (
    filters: LeadAnalyticsFilters,
  ): Promise<LeadStageSummaryPaginatedResponse> => {
    try {
      const listQueryParams = this.buildQueryParams(filters, true);
      const analyticsQueryParams = this.buildQueryParams(filters, false);

      const [listResponse, averagesResponse, summaryResponse] = await Promise.all([
        getAPI(`/v1/lead-management/leads?${listQueryParams.toString()}`),
        getAPI(`/v1/lead-management/lead-stage-averages?${analyticsQueryParams.toString()}`),
        getAPI(
          `/v1/lead-management/lead-stage-status-summary?${analyticsQueryParams.toString()}`,
        ),
      ]);

      const listResult = listResponse.result as LeadStageSummaryListResponse;
      const averagesResult = averagesResponse.result as LeadStageAveragesResponse;
      const summaryResult = summaryResponse.result as LeadStageStatusSummaryResponse;

      return {
        ...listResult,
        averages: averagesResult.averages,
        probability_stage_summary: summaryResult.probability_stage_summary,
        section: summaryResult.section || filters.section,
      };
    } catch (error) {
      throw error;
    }
  };

  getLeadOwners = async (): Promise<FilterValue[]> => {
    try {
      const response = (await getAPI(
        "/v5/sales-analytics/owners",
      )) as NewAPIResponse<{ list: FilterValue[] }>;

      const owners = response.result?.list || [];
      return owners.map((owner) => ({
        id: String(owner.id),
        value: owner.value,
      }));
    } catch (error) {
      throw error;
    }
  };

  getLeadPipelines = async (): Promise<FilterValue[]> => {
    try {
      const response = (await getAPI(
        "/v1/lead-management/pipelines",
      )) as NewAPIResponse<FilterValue[]>;

      const pipelines = response.result || [];
      return pipelines.map((pipeline) => ({
        id: String(pipeline.id),
        value: pipeline.value,
      }));
    } catch (error) {
      throw error;
    }
  };

  getLeadSources = async (): Promise<FilterValue[]> => {
    try {
      const response = (await getAPI(
        "/v1/lead-management/sources",
      )) as NewAPIResponse<FilterValue[]>;

      const sources = response.result || [];
      return sources.map((source) => ({
        id: String(source.id),
        value: source.value,
      }));
    } catch (error) {
      throw error;
    }
  };
}
